import { useEffect, useMemo, useRef, useState } from "react";
import {
    UnlinkedIntakeToProgramme,
    setLinkedProgrammeVersion,
} from "../services/ProgramService";
import { lockGradeProgrammeToggle } from "../services/GradeSettingService";
import {
    buildVersionOptions,
    getActionCopy,
} from "../utils/LinkedProgrammeUtils";
import {
    useLinkedProgrammeFilters,
    useLinkedProgrammeList,
    usePmcVersionListForFilters,
} from "../query/hooks/QueryHooksLinkedProgrammes";

/**
 * Drives the Linked Programmes tab: filters, pagination, selection, grade lock
 * toggling and bulk confirmation actions for programmes linked to a PMC record.
 * @param {object} options.detail The PMC programme detail record.
 * @param {boolean} options.QAview Whether the QA (vs Standard) view is active.
 * @param {boolean} options.isVersionEnabled Whether version overrides are enabled.
 * @param {object} options.gradeLockLoading Grade lock/unlock in-flight state (keyed by row id/lockAll/unlockAll) and its `setGradeLockLoading` setter.
 * @param {Array} options.linkedQualifications Ids of all matching linked qualifications, and its `setLinkedQualifications` setter.
 * @param {boolean} options.updateData Flag toggled to force a table refresh, and its `setUpdateData` setter.
 * @param {string|number} [options.versionId] When set (reached via a specific version's own
 *   nav), pre-selects that version in the Version filter as a starting point. Per AP-1211 AC10,
 *   the Version filter must stay visible and clearable "exactly as today" like every other
 *   filter, so this only seeds its initial value - it does not lock or hide the control.
 */
function useLinkedProgrammes({
    detail,
    QAview,
    isVersionEnabled,
    id,
    versionId,
    gradeLockLoading,
    setGradeLockLoading,
    linkedQualifications,
    setLinkedQualifications,
    updateData,
    setUpdateData,
}) {
    const [search, setSearch] = useState("");
    /*
     * {arr, checkObj} — the same shape Tablefilter (and every other table's
     * filters, e.g. Career Library) uses, so this toolbar can share that
     * exact component instead of a bespoke single-value dropdown.
     */
    const [intake, setIntake] = useState({ arr: [], checkObj: {} });
    const [campus, setCampus] = useState({ arr: [], checkObj: {} });
    const [delivery, setDelivery] = useState({ arr: [], checkObj: {} });
    const [gradesLocked, setGradesLocked] = useState({ arr: [], checkObj: {} });
    const [version, setVersion] = useState(
        versionId ? { arr: [versionId], checkObj: { [versionId]: true } } : { arr: [], checkObj: {} }
    );

    const [refreshKey, setRefreshKey] = useState(false);

    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [sortKey, setSortKey] = useState("name");
    const [sortOrder, setSortOrder] = useState("ASC");

    const [selectedIds, setSelectedIds] = useState([]);
    const [confirmation, setConfirmation] = useState(null);
    const [progress, setProgress] = useState(null);
    const [setVersionValue, setSetVersionValue] = useState("");

    const cancelRemainingRef = useRef(false);

    const isProcessing = Boolean(progress);

    const isDetailLoaded = Boolean(detail && Object.keys(detail).length > 0);

    const allMatchingIds = useMemo(
        () =>
            Array.from(
                new Set(
                    (linkedQualifications || []).map((value) =>
                        Number.isNaN(Number(value)) ? value : Number(value)
                    )
                )
            ),
        [linkedQualifications]
    );

    const allMatchingSelected =
        allMatchingIds.length > 0 &&
        selectedIds.length === allMatchingIds.length;

    const someMatchingSelected =
        selectedIds.length > 0 &&
        !allMatchingSelected;

    /*
     * QAview is deliberately excluded: Standard/QA View share the same underlying
     * query (only the displayed columns differ - see LinkedProgrammesTable), so
     * toggling it must not reset selection/pagination or wipe allMatchingIds.
     * getListData below doesn't depend on QAview either, so if it were included
     * here, toggling the view would clear linkedQualifications with nothing to
     * repopulate it - hiding the top Lock/Unlock All Grades buttons until some
     * other filter change (or a reload) fetched the list again.
     */
    const filterSignature =
        `${search}|${intake.arr.join(",")}|${campus.arr.join(",")}|${delivery.arr.join(",")}|${gradesLocked.arr.join(",")}|${version.arr.join(",")}`;

    const netsuiteProductId = detail?.netsuite_product;

    const { data: dropdown } = useLinkedProgrammeFilters(id, netsuiteProductId);

    const { data: pmcVersionList } = usePmcVersionListForFilters(id);

    const listRequestData = useMemo(() => ({
        programme_id: id,
        netsuite_product_id: netsuiteProductId,
        isAssigned: 1,
        page,
        limit: perPage,
        key: sortKey,
        sort: sortOrder,
        search,
        viaCampus: campus.arr,
        intake: intake.arr,
        viaLearnType: delivery.arr,
        viaCourseType: [],
        viaGradesLocked: gradesLocked.arr,
        viaVersion: version.arr,
        viaModule: [],
    }), [
        id,
        netsuiteProductId,
        page,
        perPage,
        sortKey,
        sortOrder,
        search,
        campus,
        intake,
        delivery,
        gradesLocked,
        version,
    ]);

    const {
        data: { listData, linkedQualifications: fetchedLinkedQualifications, totalRows },
        isLoading: loading,
    } = useLinkedProgrammeList(listRequestData, `${refreshKey}|${updateData}`, isDetailLoaded);

    const totalPages = Math.max(
        1,
        Math.ceil(totalRows / perPage)
    );

    const rangeStart =
        totalRows === 0
            ? 0
            : (page - 1) * perPage + 1;

    const rangeEnd = Math.min(
        page * perPage,
        totalRows
    );

    const versionOptions = useMemo(
        () =>
            buildVersionOptions(
                pmcVersionList.map((pmcVersion) => ({
                    value: pmcVersion.id,
                    label: pmcVersion.name,
                    /* Number(...) coercion matches the Versions list's id/version_id comparison */
                    active: Number(pmcVersion.id) === Number(pmcVersion.version_id),
                }))
            ),
        [pmcVersionList]
    );

    const confirmCopy = useMemo(() => {
        if (!confirmation) {
            return null;
        }

        return getActionCopy({
            type: confirmation.type,
            count: confirmation.ids?.length || 0,
            setVersionValue,
        });
    }, [confirmation, setVersionValue]);

    useEffect(() => {
        setSelectedIds([]);
        setPage(1);
        setLinkedQualifications([]);
    }, [filterSignature, setLinkedQualifications]);

    useEffect(() => {
        setLinkedQualifications(fetchedLinkedQualifications);
    }, [fetchedLinkedQualifications, setLinkedQualifications]);

    const emptyFilterState = { arr: [], checkObj: {} };

    const resetFilters = () => {
        setSearch("");
        setIntake(emptyFilterState);
        setCampus(emptyFilterState);
        setDelivery(emptyFilterState);
        setGradesLocked(emptyFilterState);
        setVersion(emptyFilterState);
        setPage(1);
    };

    const toggleRow = (rowId) => {
        setSelectedIds((current) =>
            current.includes(rowId)
                ? current.filter((value) => value !== rowId)
                : [...current, rowId]
        );
    };

    const toggleAllMatching = () => {
        setSelectedIds(
            allMatchingSelected ? [] : allMatchingIds
        );
    };

    const changeSort = (field) => {
        setPage(1);
        setSortKey(field);
        setSortOrder((current) =>
            sortKey === field && current === "ASC"
                ? "DESC"
                : "ASC"
        );
    };

    const handleLockGrade = async (
        rowId,
        currentStatus
    ) => {
        setGradeLockLoading((current) => ({
            ...current,
            [rowId]: true,
        }));

        try {
            await lockGradeProgrammeToggle(
                rowId,
                // 1 = grades locked, 0 = grades unlocked; toggle to the opposite state
                Number(currentStatus) === 1 ? 0 : 1,
                id
            );

            setUpdateData((current) => !current);
        } catch (error) {
            console.error(
                "useLinkedProgrammes.js->handleLockGrade->Error",
                error
            );
        } finally {
            setGradeLockLoading((current) => ({
                ...current,
                [rowId]: false,
            }));
        }
    };

    const unlinkOne = async (programmeId) => {
        await UnlinkedIntakeToProgramme({
            programme_id: programmeId,
            netsuite_id: detail?.netsuite_product,
            PMC_programme_id: id,
        });
    };

    const startConfirmation = (
        type,
        ids,
        row
    ) => {
        if (type !== "setVersion") {
            setSetVersionValue("");
        }

        setConfirmation({
            type,
            ids,
            row,
        });
    };

    const runConfirmedAction = async () => {
        if (!confirmation?.ids?.length) {
            return;
        }

        const ids = confirmation.ids;
        const type = confirmation.type;
        const copy = getActionCopy({
            type,
            count: ids.length,
            setVersionValue,
        });

        cancelRemainingRef.current = false;
        setConfirmation(null);

        setProgress({
            label: copy.label,
            current: ids.length > 0 ? 1 : 0,
            total: ids.length,
            type,
        });

        try {
            if (type === "setVersion") {
                // 1 = pin to versionIdParam, 0 = clear the override and use the active version
                const overridePmcVersion =
                    setVersionValue === "active" ? 0 : 1;
                const versionIdParam =
                    setVersionValue === "active"
                        ? null
                        : setVersionValue;

                await setLinkedProgrammeVersion(
                    ids.join(","),
                    id,
                    versionIdParam,
                    overridePmcVersion
                );

                setProgress({
                    label: copy.label,
                    current: ids.length,
                    total: ids.length,
                    type,
                });

            } else {
                for (
                    let index = 0;
                    index < ids.length;
                    index += 1
                ) {
                    if (cancelRemainingRef.current) {
                        break;
                    }

                    const programmeId = ids[index];

                    setProgress({
                        label: copy.label,
                        current: index + 1,
                        total: ids.length,
                        type,
                    });

                    if (type === "unlink") {
                        await unlinkOne(programmeId);
                    }

                    if (type === "lock") {
                        await lockGradeProgrammeToggle(
                            programmeId,
                            1, // 1 = lock grades
                            id
                        );
                    }

                    if (type === "unlock") {
                        await lockGradeProgrammeToggle(
                            programmeId,
                            0, // 0 = unlock grades
                            id
                        );
                    }
                }
            }

            setSelectedIds([]);
            setUpdateData((current) => !current);
        } catch (error) {
            console.error(
                "useLinkedProgrammes.js->runConfirmedAction->Error",
                error
            );
        } finally {
            window.setTimeout(
                () => setProgress(null),
                250
            );
        }
    };

    const refresh = () =>
        setRefreshKey((current) => !current);

    return {
        id,
        search,
        setSearch,
        intake,
        setIntake,
        campus,
        setCampus,
        delivery,
        setDelivery,
        gradesLocked,
        setGradesLocked,
        version,
        setVersion,
        loading,
        listData,
        dropdown,
        refreshKey,
        setRefreshKey,
        refresh,
        perPage,
        setPerPage,
        page,
        setPage,
        sortKey,
        sortOrder,
        totalRows,
        totalPages,
        rangeStart,
        rangeEnd,
        selectedIds,
        setSelectedIds,
        allMatchingSelected,
        someMatchingSelected,
        toggleRow,
        toggleAllMatching,
        changeSort,
        resetFilters,
        handleLockGrade,
        gradeLockLoading,
        isProcessing,
        progress,
        cancelRemainingRef,
        confirmation,
        setConfirmation,
        startConfirmation,
        runConfirmedAction,
        confirmCopy,
        setVersionValue,
        setSetVersionValue,
        versionOptions,
        isVersionEnabled,
        QAview,
    };
}

export default useLinkedProgrammes;
